<template>
  <div class="container mx-auto px-4 py-8">
    <div class="bg-white rounded-xl shadow-xl p-8 mb-10 transform transition-all hover:shadow-2xl">
      <h1 class="text-3xl font-bold text-gray-800 mb-2">Free Online Image Compression Tool</h1>
      <p class="text-lg text-blue-600 font-medium mb-2">Unlimited free image compressor online/offline</p>
      <p class="text-gray-600 mb-6">Supports PNG and JPEG formats up to 10MB</p>
      
      <div 
        class="border-2 border-dashed border-indigo-300 rounded-xl p-8 text-center hover:border-indigo-500 transition-colors cursor-pointer bg-gradient-to-br from-gray-50 to-indigo-50"
        :class="{ 'border-indigo-500 bg-indigo-50': isDragging }"
        @dragover.prevent="isDragging = true"
        @dragleave.prevent="isDragging = false"
        @drop.prevent="handleFileDrop"
        @click="triggerFileInput"
      >
        <input 
          type="file" 
          ref="fileInput" 
          accept="image/png,image/jpeg,image/jpg" 
          @change="handleFileSelect"
          multiple
          class="hidden"
        />
        <div v-if="isUploading" class="flex flex-col items-center">
          <div class="w-12 h-12 border-t-3 border-indigo-500 rounded-full animate-spin mb-4"></div>
          <p class="text-gray-700 mb-2 text-lg">Optimizing your images...</p>
          <div v-if="selectedFiles.length > 1" class="w-full max-w-md">
            <div class="flex justify-between text-sm text-gray-600 mb-1">
              <span>Processing images</span>
              <span>{{ processedCount }} of {{ selectedFiles.length }}</span>
            </div>
            <div class="w-full bg-gray-200 rounded-full h-2">
              <div 
                class="bg-indigo-500 h-2 rounded-full"
                :style="{ width: `${(processedCount / selectedFiles.length) * 100}%` }"
              ></div>
            </div>
          </div>
          <div v-else class="w-full max-w-md bg-gray-200 rounded-full h-2 mt-2">
            <div class="bg-indigo-500 h-2 rounded-full animate-pulse"></div>
          </div>
        </div>
        <div v-else-if="!selectedFiles.length" class="flex flex-col items-center">
          <svg xmlns="http://www.w3.org/2000/svg" width="80" height="80" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-indigo-400 mb-4">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="17 8 12 3 7 8"></polyline>
            <line x1="12" y1="3" x2="12" y2="15"></line>
          </svg>
          <p class="text-gray-700 font-medium mb-2 text-xl">Drag & drop images here or click to browse</p>
          <p class="text-sm text-gray-500">PNG, JPEG up to 10MB (multiple files supported)</p>
        </div>
        <div v-else class="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
          <div v-for="(file, index) in selectedFiles" :key="index" class="relative bg-white rounded-xl overflow-hidden border border-gray-200 shadow-md hover:shadow-lg transition-shadow">
            <div class="aspect-w-4 aspect-h-3 w-full">
              <img :src="selectedFilePreviews[index]" alt="Preview" class="object-cover w-full h-full" />
            </div>
            <div class="p-3 text-left">
              <p class="truncate text-sm font-medium text-gray-800">{{ file.name }}</p>
              <p class="text-xs text-gray-500">{{ formatFileSize(file.size) }}</p>
              <button @click.stop="removeFile(index)" class="absolute top-2 right-2 bg-gray-800 bg-opacity-70 text-white rounded-full p-1.5 hover:bg-red-500 transition-colors">
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
      
      <div class="bg-red-50 text-red-600 p-4 rounded-xl mt-6 flex items-center" v-if="error">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <span>{{ error }}</span>
      </div>
    </div>

    <section class="mb-16">
      <div class="grid grid-cols-1 md:grid-cols-3 gap-8">
        <div class="bg-white rounded-xl p-8 shadow-xl hover:shadow-2xl transition transform hover:-translate-y-1">
          <div class="w-16 h-16 bg-indigo-100 rounded-full flex items-center justify-center text-indigo-600 mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-800 mb-3">Easy Upload</h3>
          <p class="text-gray-600">Drag and drop images or select from your device</p>
        </div>
        <div class="bg-white rounded-xl p-8 shadow-xl hover:shadow-2xl transition transform hover:-translate-y-1">
          <div class="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center text-green-600 mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M13 2L3 14h9l-1 8 10-12h-9l1-8z"></path>
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-800 mb-3">Fast Compression</h3>
          <p class="text-gray-600">Quickly reduce file size while maintaining quality</p>
        </div>
        <div class="bg-white rounded-xl p-8 shadow-xl hover:shadow-2xl transition transform hover:-translate-y-1">
          <div class="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center text-purple-600 mb-6">
            <svg xmlns="http://www.w3.org/2000/svg" width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </div>
          <h3 class="text-xl font-bold text-gray-800 mb-3">Instant Download</h3>
          <p class="text-gray-600">Save your optimized images with one click</p>
        </div>
      </div>
    </section>
    
    <div class="bg-white rounded-xl shadow-xl p-8 mb-10" v-if="compressionResult" id="compression-results">
      <div class="flex flex-wrap justify-between items-center mb-8">
        <h2 class="text-2xl font-bold text-gray-800">Compression Results</h2>
        <div class="bg-gradient-to-r from-green-500 to-emerald-600 text-white py-2 px-4 rounded-full text-sm font-medium" v-if="totalSavedPercentage > 0">
          <span>{{ totalSavedPercentage }}% Smaller</span>
        </div>
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div class="bg-gray-50 p-5 rounded-xl">
          <span class="block text-sm text-gray-500 mb-1">Original</span>
          <span class="text-lg font-semibold text-gray-900">{{ formatFileSize(totalOriginalSize) }}</span>
        </div>
        <div class="bg-gray-50 p-5 rounded-xl">
          <span class="block text-sm text-gray-500 mb-1">Compressed</span>
          <span class="text-lg font-semibold text-gray-900">{{ formatFileSize(totalCompressedSize) }}</span>
        </div>
        <div class="bg-green-50 p-5 rounded-xl">
          <span class="block text-sm text-green-600 mb-1">Saved</span>
          <span class="text-lg font-semibold text-green-700">{{ formatFileSize(totalSavedBytes) }}</span>
        </div>
      </div>
      
      <div class="flex items-center bg-green-50 text-green-700 p-5 rounded-xl mb-8">
        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-3">
          <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
          <polyline points="22 4 12 14.01 9 11.01"></polyline>
        </svg>
        <p class="font-medium">Images compressed successfully!</p>
      </div>
      
      <div class="overflow-x-auto mb-8 rounded-xl shadow border border-gray-200">
        <table class="min-w-full divide-y divide-gray-200">
          <thead class="bg-gray-50">
            <tr>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Filename</th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Original</th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Compressed</th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Saved</th>
              <th scope="col" class="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
            </tr>
          </thead>
          <tbody class="bg-white divide-y divide-gray-200">
            <tr v-for="(result, index) in compressionResults" :key="index" class="hover:bg-gray-50 transition">
              <td class="px-6 py-4 whitespace-nowrap">
                <div class="flex items-center">
                  <img :src="result.compressedUrl" class="w-12 h-12 object-cover mr-4 rounded-lg" alt="Thumbnail" />
                  <span class="text-sm font-medium text-gray-900 truncate max-w-xs">{{ result.fileName }}</span>
                </div>
              </td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatFileSize(result.originalSize) }}</td>
              <td class="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{{ formatFileSize(result.compressedSize) }}</td>
              <td class="px-6 py-4 whitespace-nowrap">
                <span class="inline-flex items-center px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">{{ result.savedPercentage }}%</span>
              </td>
              <td class="px-6 py-4 whitespace-nowrap">
                <button class="text-indigo-600 hover:text-indigo-800 p-2 rounded-full hover:bg-indigo-100 transition" @click="downloadSingleImage(result)">
                  <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                    <polyline points="7 10 12 15 17 10"></polyline>
                    <line x1="12" y1="15" x2="12" y2="3"></line>
                  </svg>
                </button>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
      
      <div class="flex flex-col sm:flex-row gap-4">
        <button class="bg-gradient-to-r from-indigo-500 to-blue-600 hover:from-indigo-600 hover:to-blue-700 text-white py-4 px-6 rounded-xl font-medium flex items-center justify-center transition focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 disabled:opacity-70 shadow-lg" @click="downloadImages" :disabled="isDownloading">
          <div v-if="isDownloading" class="w-5 h-5 border-t-2 border-white rounded-full animate-spin mr-2"></div>
          <template v-else>
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="mr-2">
              <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
              <polyline points="7 10 12 15 17 10"></polyline>
              <line x1="12" y1="15" x2="12" y2="3"></line>
            </svg>
          </template>
          {{ isDownloading ? 'Creating Zip...' : 'Download Compressed Images' }}
        </button>
        
        <button class="border border-gray-300 hover:bg-gray-50 text-gray-700 py-4 px-6 rounded-xl font-medium transition focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-opacity-50 shadow" @click="clearImage">
          Optimize Another Image
        </button>
      </div>
    </div>

    <!-- NEW SEO Content Section -->
    <section class="bg-white rounded-xl shadow-xl p-8 mb-10">
      <h2 class="text-3xl font-bold text-gray-800 mb-6 text-center">Why Image Compression Matters</h2>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
        <div>
          <h3 class="text-xl font-bold text-indigo-700 mb-4">Faster Website Loading Times</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            Large image files are one of the biggest culprits behind slow website loading times. By reducing the file size of your images, you can dramatically improve page speed - a critical factor for both user experience and search engine rankings.
          </p>
          <p class="text-gray-700 leading-relaxed">
            Studies show that users abandon websites that take more than 3 seconds to load. With our image compression tool, you can reduce image sizes by up to 70% without noticeable quality loss, keeping your visitors engaged and improving conversion rates.
          </p>
        </div>
        <div>
          <h3 class="text-xl font-bold text-indigo-700 mb-4">Lower Bandwidth Usage</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            Compressing your images means less data to transfer when someone visits your website. This is especially important for users on mobile devices or with limited data plans, helping to make your content more accessible.
          </p>
          <p class="text-gray-700 leading-relaxed">
            Reduced bandwidth usage also means lower hosting costs for high-traffic websites and improved performance for your overall web infrastructure. Our optimizer uses advanced algorithms to find the perfect balance between quality and file size.
          </p>
        </div>
      </div>
      
      <div class="grid grid-cols-1 md:grid-cols-2 gap-10 mb-12">
        <div>
          <h3 class="text-xl font-bold text-indigo-700 mb-4">Improved SEO Performance</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            Search engines like Google consider page speed as a ranking factor. Faster loading websites due to optimized images tend to rank higher in search results, increasing your visibility and organic traffic.
          </p>
          <p class="text-gray-700 leading-relaxed">
            Additionally, properly compressed images improve Core Web Vitals metrics like Largest Contentful Paint (LCP) and Cumulative Layout Shift (CLS), which are key factors in Google's ranking algorithm.
          </p>
        </div>
        <div>
          <h3 class="text-xl font-bold text-indigo-700 mb-4">Better User Experience</h3>
          <p class="text-gray-700 leading-relaxed mb-4">
            Nobody likes waiting for images to load. Optimized images create a smoother, more responsive browsing experience for your visitors, leading to lower bounce rates and higher engagement metrics.
          </p>
          <p class="text-gray-700 leading-relaxed">
            For e-commerce sites, image optimization is particularly crucial as product images need to load quickly while maintaining high quality to showcase products effectively. Our compression tool preserves visual details while reducing file size.
          </p>
        </div>
      </div>
      
      <div class="bg-indigo-50 p-6 rounded-xl">
        <h3 class="text-xl font-bold text-indigo-700 mb-4">How Our Image Compressor Works</h3>
        <p class="text-gray-700 leading-relaxed mb-4">
          Our image compression tool uses advanced algorithms to analyze and optimize your images without sacrificing quality. For PNG files, we implement smart color palette optimization and lossless compression techniques. For JPEGs, we apply intelligent quality adjustments and metadata optimization.
        </p>
        <p class="text-gray-700 leading-relaxed mb-4">
          Unlike many other online image compressors, our tool works entirely in your browser - your images are never uploaded to a server, ensuring complete privacy and security. This also means compression happens instantly, with no waiting for uploads or downloads.
        </p>
        <div class="flex flex-wrap gap-4 mt-6">
          <div class="flex items-center bg-white py-2 px-4 rounded-lg shadow-sm">
            <span class="text-indigo-600 font-semibold mr-2">✓</span>
            <span class="text-gray-700">No file upload limits</span>
          </div>
          <div class="flex items-center bg-white py-2 px-4 rounded-lg shadow-sm">
            <span class="text-indigo-600 font-semibold mr-2">✓</span>
            <span class="text-gray-700">100% secure processing</span>
          </div>
          <div class="flex items-center bg-white py-2 px-4 rounded-lg shadow-sm">
            <span class="text-indigo-600 font-semibold mr-2">✓</span>
            <span class="text-gray-700">Maintains image quality</span>
          </div>
          <div class="flex items-center bg-white py-2 px-4 rounded-lg shadow-sm">
            <span class="text-indigo-600 font-semibold mr-2">✓</span>
            <span class="text-gray-700">Batch processing</span>
          </div>
        </div>
      </div>
    </section>

    <!-- FAQ Section -->
    <section class="bg-white rounded-xl shadow-xl p-8 mb-10">
      <h2 class="text-3xl font-bold text-gray-800 mb-8 text-center">Frequently Asked Questions</h2>
      
      <div class="mb-8">
        <div class="relative">
          <input 
            type="text" 
            placeholder="Search frequently asked questions..." 
            v-model="searchQuery"
            @input="filterQuestions"
            class="w-full py-4 px-5 pl-12 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 shadow-sm"
          />
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        </div>
      </div>
      
      <div class="space-y-5">
        <div 
          v-for="(item, index) in filteredQuestions" 
          :key="index" 
          class="border border-gray-200 rounded-xl overflow-hidden shadow-sm hover:shadow-md transition"
          :class="{ 'border-indigo-500': activeIndex === index }"
        >
          <div 
            class="p-5 flex justify-between items-center cursor-pointer transition hover:bg-gray-50"
            :class="{ 'bg-indigo-500 text-white hover:bg-indigo-600': activeIndex === index }"
            @click="toggleAccordion(index)"
          >
            <h3 class="font-medium text-lg">{{ item.question }}</h3>
            <svg 
              xmlns="http://www.w3.org/2000/svg" 
              width="20" 
              height="20" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              stroke-width="2" 
              stroke-linecap="round" 
              stroke-linejoin="round"
              class="transition-transform duration-300"
              :class="{ 'transform rotate-180': activeIndex === index }"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>
          <div 
            class="overflow-hidden transition-all duration-300"
            :class="{ 'h-0': activeIndex !== index }"
          >
            <div class="p-5 bg-gray-50" v-if="activeIndex === index">
              <p class="text-gray-700 leading-relaxed" v-html="item.answer"></p>
            </div>
          </div>
        </div>
      </div>
      
      <div v-if="filteredQuestions.length === 0" class="flex flex-col items-center justify-center p-10 bg-gray-50 rounded-xl text-center shadow-inner">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="text-gray-400 mb-4">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="8" x2="12" y2="12"></line>
          <line x1="12" y1="16" x2="12.01" y2="16"></line>
        </svg>
        <p class="text-gray-600 text-lg">No questions found matching your search. Try a different search term.</p>
      </div>
    </section>
  </div>
</template>

<script>
import { batchCompressImages, downloadCompressedImage, downloadAsZip } from '../utils'
import { useMeta } from 'vue-meta'

export default {
  name: 'Home',
  setup() {
    useMeta({
      title: 'Free Online Image Compression Tool - Optimize PNG and JPEG',
      meta: [
        { name: 'description', content: 'Free online image compression tool to optimize and reduce the file size of your PNG and JPEG images. No upload limits, fast processing, and secure client-side compression.' },
        { name: 'keywords', content: 'image compression, PNG compressor, JPEG optimizer, reduce image size, optimize images, free image compression' },
        { property: 'og:site_name', content: 'Free Image Resize' },
        { property: 'og:type', content: 'website' },
        { property: 'og:url', content: 'https://free-image-resize.com' },
        { property: 'og:title', content: 'Free Online Image Compression Tool - Optimize PNG and JPEG' },
        { property: 'og:description', content: 'Compress and optimize your PNG and JPEG images online for free. Reduce file size while maintaining quality, no upload required.' },
        { property: 'og:image', content: '/og-image.jpeg' },
        { property: 'og:image:width', content: '1200' },
        { property: 'og:image:height', content: '630' },
        { property: 'og:image:alt', content: 'Free Image Resize - Online Image Compression Tool' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:site', content: '@freeimageresizer' },
        { name: 'twitter:title', content: 'Free Online Image Compression Tool' },
        { name: 'twitter:description', content: 'Compress PNG and JPEG images online for free while maintaining quality. No upload required.' },
        { name: 'twitter:image', content: '/og-image.jpeg' },
        { name: 'twitter:image:alt', content: 'Free Image Resize Tool Screenshot' }
      ]
    })
    
    // Add JSON-LD structured data
    const addStructuredData = () => {
      const structuredData = {
        "@context": "https://schema.org",
        "@type": "SoftwareApplication",
        "name": "Free Image Compression Tool",
        "applicationCategory": "WebApplication",
        "operatingSystem": "All",
        "offers": {
          "@type": "Offer",
          "price": "0",
          "priceCurrency": "USD"
        },
        "description": "Free online tool to compress and optimize PNG and JPEG images while maintaining quality. No upload required.",
        "aggregateRating": {
          "@type": "AggregateRating",
          "ratingValue": "4.8",
          "ratingCount": "1247"
        },
        "image": "/og-image.jpeg",
        "screenshot": "/og-image.jpeg",
        "url": "https://free-image-resize.com",
        "author": {
          "@type": "Organization",
          "name": "Free Image Resize",
          "url": "https://free-image-resize.com"
        }
      }
      
      const script = document.createElement('script')
      script.type = 'application/ld+json'
      script.innerHTML = JSON.stringify(structuredData)
      document.head.appendChild(script)
    }
    
    return { addStructuredData }
  },
  data() {
    return {
      selectedFiles: [],
      selectedFilePreviews: [],
      isDragging: false,
      isUploading: false,
      compressionResults: [],
      error: null,
      imageLoadErrors: {
        original: false,
        compressed: false
      },
      processedCount: 0,
      isDownloading: false,
      // FAQ related data
      searchQuery: '',
      activeIndex: null,
      faqItems: [
        {
          question: "What image formats does ImageOptimizer support?",
          answer: "ImageOptimizer currently supports PNG and JPEG image formats. We plan to add support for additional formats like WebP and GIF in future updates."
        },
        {
          question: "Is there a file size limit for uploads?",
          answer: "Yes, the maximum file size for uploads is 10MB per image. This limit ensures fast processing times and optimal service performance."
        },
        {
          question: "How does the compression algorithm work?",
          answer: "Our compression technology uses advanced algorithms that intelligently analyze each image to identify areas where file size can be reduced while maintaining visual quality. The exact approach varies based on the image format:<br><br>- For PNG, we optimize the color palette, apply adaptive filtering, and use efficient compression methods.<br>- For JPEG, we optimize quantization tables, apply intelligent color sampling, and remove unnecessary metadata."
        },
        {
          question: "Will compression affect the quality of my images?",
          answer: "Our algorithms are designed to maintain visual quality while reducing file size. However, compression always involves some trade-offs. We use techniques that prioritize preserving details that are most noticeable to the human eye. For most web and digital uses, the quality difference is negligible while achieving significant file size reductions."
        },
        {
          question: "Is my data secure when I upload images?",
          answer: "Yes, we take data security seriously. Your images are processed on secure servers and are automatically deleted shortly after processing. We do not store or retain your images for any longer than necessary to provide the service. We also do not claim any rights to your content."
        },
        {
          question: "Can I compress multiple images at once?",
          answer: "Yes, our batch processing feature allows you to upload and compress multiple images simultaneously. You can also download all compressed images as a zip file."
        },
        {
          question: "Are there any limitations on how I can use the compressed images?",
          answer: "There are no restrictions on how you can use your compressed images. You retain all rights to your images, and you're free to use them in any way you choose - whether for personal, commercial, or any other purposes."
        },
        {
          question: "Do you offer an API for developers?",
          answer: "We're currently developing an API that will allow developers to integrate our image compression technology into their applications. If you're interested in being notified when this becomes available, please contact us."
        },
        {
          question: "Why would I need to compress my images?",
          answer: "Compressing images offers several benefits:<br><br>- <strong>Faster website loading times</strong>: Smaller images load more quickly, improving user experience and SEO rankings.<br>- <strong>Reduced storage requirements</strong>: Save space on your servers or devices.<br>- <strong>Lower bandwidth usage</strong>: Especially important for websites with high traffic or users with limited data plans.<br>- <strong>Improved user experience</strong>: Faster-loading images lead to lower bounce rates and higher engagement."
        },
        {
          question: "Is ImageOptimizer free to use?",
          answer: "Yes, the basic version of ImageOptimizer is completely free to use. We may introduce premium features in the future, but our core compression functionality will always remain free."
        }
      ],
      filteredQuestions: []
    }
  },
  computed: {
    compressionResult() {
      // For backward compatibility, return the first result
      return this.compressionResults.length > 0 ? this.compressionResults[0] : null;
    },
    totalOriginalSize() {
      return this.compressionResults.reduce((total, result) => total + result.originalSize, 0);
    },
    totalCompressedSize() {
      return this.compressionResults.reduce((total, result) => total + result.compressedSize, 0);
    },
    totalSavedBytes() {
      return this.compressionResults.reduce((total, result) => total + result.savedBytes, 0);
    },
    totalSavedPercentage() {
      if (this.totalOriginalSize === 0) return 0;
      return Math.round((this.totalSavedBytes / this.totalOriginalSize) * 100);
    }
  },
  created() {
    this.filteredQuestions = this.faqItems;
  },
  mounted() {
    // Add JSON-LD structured data
    this.addStructuredData();
  },
  methods: {
    // Image compression methods
    triggerFileInput() {
      this.$refs.fileInput.click();
    },
    handleFileSelect(event) {
      const files = event.target.files;
      if (files.length) {
        this.processFiles(Array.from(files));
        // Automatically start processing when files are selected
        this.uploadImage();
      }
    },
    handleFileDrop(event) {
      this.isDragging = false;
      const files = event.dataTransfer.files;
      if (files.length) {
        this.processFiles(Array.from(files));
        // Automatically start processing when files are dropped
        this.uploadImage();
      }
    },
    processFiles(files) {
      // Filter out invalid files and add valid ones
      const validFiles = [];
      const invalidFileTypes = [];
      const oversizedFiles = [];
      
      for (const file of files) {
        // Check if file is a PNG or JPEG
        if (!['image/png', 'image/jpeg', 'image/jpg'].includes(file.type)) {
          invalidFileTypes.push(file.name);
          continue;
        }
        
        // Check file size (max 10MB)
        if (file.size > 10 * 1024 * 1024) {
          oversizedFiles.push(file.name);
          continue;
        }
        
        validFiles.push(file);
      }
      
      // Add valid files to selected files
      for (const file of validFiles) {
        this.addFilePreview(file);
      }
      
      // Show errors for invalid files
      if (invalidFileTypes.length || oversizedFiles.length) {
        let errorMessage = '';
        
        if (invalidFileTypes.length) {
          errorMessage += `Unsupported file format: ${invalidFileTypes.join(', ')}. `;
        }
        
        if (oversizedFiles.length) {
          errorMessage += `Files exceed 10MB limit: ${oversizedFiles.join(', ')}`;
        }
        
        this.error = errorMessage;
      } else {
        this.error = null;
      }
    },
    addFilePreview(file) {
      this.selectedFiles.push(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onload = (e) => {
        this.selectedFilePreviews.push(e.target.result);
      };
      reader.readAsDataURL(file);
    },
    removeFile(index) {
      this.selectedFiles.splice(index, 1);
      this.selectedFilePreviews.splice(index, 1);
    },
    clearImage() {
      this.selectedFiles = [];
      this.selectedFilePreviews = [];
      this.compressionResults = [];
      this.error = null;
      this.imageLoadErrors = {
        original: false,
        compressed: false
      };
      // Clear the file input
      this.$refs.fileInput.value = '';
    },
    async uploadImage() {
      if (!this.selectedFiles.length) return;
      
      this.isUploading = true;
      this.error = null;
      this.compressionResults = [];
      this.imageLoadErrors = {
        original: false,
        compressed: false
      };
      this.processedCount = 0;
      
      try {
        // Client-side image compression using our utility
        const results = await batchCompressImages(this.selectedFiles, {
          maxWidth: 1920,
          maxHeight: 1920,
          quality: 0.6
        });
        
        // Update the UI with results
        this.compressionResults = results.map(result => {
          if (!result.success) {
            console.error('Error compressing image:', result.error);
            // Create a basic result object for failed compressions
            return {
              success: false,
              fileName: result.fileName,
              error: result.error,
              originalSize: 0,
              compressedSize: 0,
              savedBytes: 0,
              savedPercentage: 0
            };
          }
          
          return {
            ...result,
            downloadUrl: result.compressedUrl // Use the compressedUrl as the download URL
          };
        });
        
        // Scroll to results
        setTimeout(() => {
          const resultsContainer = document.getElementById('compression-results');
          if (resultsContainer) {
            resultsContainer.scrollIntoView({ 
              behavior: 'smooth',
              block: 'start'
            });
          }
        }, 300);
      } catch (error) {
        console.error('Error compressing images:', error);
        this.error = error.message || 'Error compressing images';
      } finally {
        this.isUploading = false;
      }
    },
    downloadImages() {
      if (!this.compressionResults.length) return;
      
      try {
        // Create a zip file when there are multiple images (more than 1)
        if (this.compressionResults.length > 1) {
          this.startBatchDownload();
        } else {
          // If there's only one image, use the single image download
          this.downloadSingleImage(this.compressionResults[0]);
        }
      } catch (error) {
        console.error('Error initiating downloads:', error);
        this.error = 'Failed to download images. Please try again.';
      }
    },
    async startBatchDownload() {
      try {
        this.error = null;
        this.isDownloading = true;
        
        // Use the utility to create and download a zip
        await downloadAsZip(this.compressionResults);
        
        console.log('Batch download completed');
      } catch (error) {
        console.error('Error during batch download:', error);
        this.error = error.message || 'Failed to create zip file. Please try again or download images individually.';
      } finally {
        this.isDownloading = false;
      }
    },
    formatFileSize(bytes) {
      if (bytes < 1024) {
        return bytes + ' B';
      } else if (bytes < 1024 * 1024) {
        return (bytes / 1024).toFixed(2) + ' KB';
      } else {
        return (bytes / (1024 * 1024)).toFixed(2) + ' MB';
      }
    },
    handleImageError(event, type) {
      event.target.src = 'https://via.placeholder.com/150';
      this.imageLoadErrors[type] = true;
    },
    downloadSingleImage(result) {
      try {
        // Use the utility function for single image download
        downloadCompressedImage(result);
      } catch (error) {
        console.error('Error initiating download:', error);
        this.error = 'Failed to download the image. Please try again.';
      }
    },
    
    // FAQ methods
    toggleAccordion(index) {
      if (this.activeIndex === index) {
        this.activeIndex = null;
      } else {
        this.activeIndex = index;
      }
    },
    filterQuestions() {
      if (!this.searchQuery) {
        this.filteredQuestions = this.faqItems;
        return;
      }
      
      const query = this.searchQuery.toLowerCase();
      this.filteredQuestions = this.faqItems.filter(item => 
        item.question.toLowerCase().includes(query) || 
        item.answer.toLowerCase().includes(query)
      );
    }
  }
}
</script>

<style>
/* Add canonical link and other SEO elements */
head::after {
  content: "";
  display: none;
}
</style> 